<script setup>
import { ref, computed, provide } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import GiftBoxWizard from './GiftBoxWizard.vue'
import StepHeader from './ui/StepHeader.vue'
import HelpModal from './ui/HelpModal.vue'

const { t } = useI18n()
const router = useRouter()
const finalResult = ref(null)
const currentStep = ref(1)
const showHelpModal = ref(false)

const stepTitles = {
  1: computed(() => t('app.steps.customize')),
  2: computed(() => t('app.steps.secure')),
  3: computed(() => t('app.steps.topup'))
}

const updateCurrentStep = (step) => {
  currentStep.value = step
}

const childNavigation = ref(null)

provide('updateHeaderStep', updateCurrentStep)
provide('registerNavigation', (navFunctions) => {
  childNavigation.value = navFunctions
})

const handleGoBack = () => {
  if (childNavigation.value && childNavigation.value.goBack) {
    childNavigation.value.goBack()
  }
}

const handleHelpClick = () => {
  showHelpModal.value = true
}

const handleStepperComplete = (result) => {
  finalResult.value = result
  console.log('Deposit Box creation completed:', result)
}

const handleCreateAnother = () => {
  finalResult.value = null
  currentStep.value = 1
  // Reset the stepper by reloading the component
  window.location.reload()
}
</script>


<template>
  <div class="giftbox-app-container">
    <div class="w-full mx-auto bg-white flex flex-col relative giftbox-app-inner">
      <!-- Header -->
      <StepHeader
        v-if="!finalResult"
        :current-step="currentStep"
        :total-steps="3"
        :step-title="stepTitles[currentStep]?.value || ''"
        @go-back="handleGoBack"
        @help-click="handleHelpClick"
      />

      <!-- Content -->
      <div class="flex-1 overflow-visible pt-4 px-1 relative giftbox-content">
        <transition name="fade" mode="out-in">
          <!-- Main Stepper Flow -->
          <GiftBoxWizard
            v-if="!finalResult"
            @sharing-configured="handleStepperComplete"
          />

          <!-- Success/Completion State -->
          <div v-else class="flex flex-col gap-8 animate-slide-up">
            <div class="text-center">
              <div class="text-7xl mb-4 animate-bounce sm:text-5xl">🎉</div>
              <h2 class="m-0 mb-2 text-xl font-semibold text-vault-text-primary sm:text-lg">Deposit Box создан успешно!</h2>
              <p class="m-0 text-base text-vault-text-secondary leading-relaxed">
                Ваш {{ finalResult.currency?.name }} Deposit Box готов к использованию
              </p>
            </div>

            <div class="flex justify-center">
              <div class="bg-vault-bg-secondary border border-vault-border-light rounded-lg p-6 max-w-md w-full sm:p-4">
                <h3 class="m-0 mb-4 text-lg font-semibold text-vault-text-primary text-center">📦 Детали депозита</h3>
                <div class="flex justify-between items-start gap-4 mb-4 py-2 border-b border-vault-border-light sm:flex-col sm:items-start sm:gap-1">
                  <span class="text-sm font-medium text-vault-text-secondary min-w-[80px] sm:min-w-0">Валюта:</span>
                  <span class="text-sm text-vault-text-primary text-right break-words flex-1 sm:text-left">{{ finalResult.currency?.name }}</span>
                </div>
                <div class="flex justify-between items-start gap-4 mb-4 py-2 border-b border-vault-border-light sm:flex-col sm:items-start sm:gap-1">
                  <span class="text-sm font-medium text-vault-text-secondary min-w-[80px] sm:min-w-0">Ссылка:</span>
                  <span class="text-sm text-vault-text-primary text-right break-words flex-1 font-mono text-xs text-vault-blue-400 sm:text-left">{{ finalResult.finalUrl }}</span>
                </div>
                <div v-if="finalResult.message" class="flex justify-between items-start gap-4 py-2 sm:flex-col sm:items-start sm:gap-1">
                  <span class="text-sm font-medium text-vault-text-secondary min-w-[80px] sm:min-w-0">Сообщение:</span>
                  <span class="text-sm text-vault-text-primary text-right break-words flex-1 sm:text-left">{{ finalResult.message }}</span>
                </div>
              </div>
            </div>

            <div class="flex justify-center">
              <button @click="handleCreateAnother" class="btn-primary px-8 py-4 text-base font-semibold sm:w-full">
                Создать еще один Deposit Box
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Help Modal -->
    <HelpModal :open="showHelpModal" @close="showHelpModal = false" />
  </div>
</template>

<style scoped>
/* Container with fixed height to prevent jumping between steps */
.giftbox-app-container {
  width: 100%;
  min-height: 600px;
  display: flex;
  flex-direction: column;
}

.giftbox-app-inner {
  min-height: 600px;
  height: 100%;
}

.giftbox-content {
  min-height: 580px; /* Account for header height */
}

/* Transition animation for Vue <transition> */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .giftbox-app-container {
    min-height: 600px;
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
  }

  .giftbox-app-inner {
    min-height: 600px;
  }

  .giftbox-content {
    min-height: 500px;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>